/**
 * IndexedDB-Schema für die Offline-Speicherung.
 *
 * Strategie:
 *  - Lese-Pfad: TanStack Query holt vom Server, on-success → in IndexedDB cachen.
 *               Bei Netzwerkfehler → Fallback aus IndexedDB.
 *  - Schreib-Pfad: Optimistisches Update von Cache + IndexedDB; Operation in
 *                  `outbox` einreihen. Sync-Engine sendet später.
 *
 * Versions-Migration: Bei Schema-Änderungen die Versions-Nummer erhöhen und in
 * `db.version(N).stores({…})` eine neue Mapping-Definition ergänzen.
 */

import Dexie, { type EntityTable } from 'dexie';
import type { ApiShift } from '../lib/shifts';
import type { ApiLocation, ApiShiftTemplate } from '../lib/locations';
import type { ApiUser } from '../lib/users';
import type { ApiCalendarEntry } from '../lib/calendarEntries';

/** Aktion in der Outbox. POST/PUT/DELETE auf einer Entity-Sammlung. */
export type OutboxKind =
  | 'shift.create'
  | 'shift.update'
  | 'shift.delete'
  | 'calendarEntry.create'
  | 'calendarEntry.update'
  | 'calendarEntry.delete';

export interface ConflictInfo {
  /** Server-Version zum Zeitpunkt des 409. */
  serverVersion: ApiShift;
  /** Lokale Version (was der Client zu schreiben versucht hat). */
  localVersion: ApiShift;
  /** Wann der Konflikt erkannt wurde (lokaler ms-Timestamp). */
  detectedAt: number;
}

export interface OutboxEntry {
  /** Auto-inkrement, von Dexie gesetzt. */
  id?: number;
  /** Welche Aktion ausgeführt werden soll. */
  kind: OutboxKind;
  /**
   * Bei `create`: client-erzeugte UUID. Wird auch in der lokalen `shifts`-Tabelle
   * verwendet, bis der Server eine eigene ID zurückgibt.
   * Bei `update`/`delete`: existierende Server-ID.
   */
  entityId: string;
  /** Payload für POST/PUT (für DELETE leer). */
  payload?: unknown;
  /**
   * Server-`lastModified` der Version, auf der die Mutation basiert.
   * Wird als `X-Expected-Last-Modified`-Header mitgeschickt — Server vergleicht
   * gegen aktuelle Version, gibt bei Konflikt 409 zurück.
   * Bei `create` leer.
   */
  baseLastModified?: string;
  /** Timestamp der Erstellung der Operation (lokal). */
  createdAt: number;
  /**
   * Gesamtprüfung 11.09.2026: wer die Änderung gemacht hat und in welcher
   * Firma. Einträge einer anderen Person werden nie gesendet (geteiltes
   * Gerät), Einträge einer anderen Firma erst, wenn sie wieder aktiv ist.
   * Kein Index nötig → keine neue Dexie-Version. Fehlt bei Alt-Einträgen.
   */
  userId?: string;
  companyId?: string | null;
  /** Anzahl bisheriger Sende-Versuche. */
  attempts: number;
  /** Letzter Fehler-Text (falls vorhanden). */
  lastError?: string;
  /** Letzter Sende-Versuch (Timestamp). */
  lastAttemptAt?: number;
  /**
   * Wenn gesetzt, hängt der Eintrag in einem Konflikt fest. Die Sync-Engine
   * versucht NICHT erneut zu senden, bis der Konflikt explizit aufgelöst wurde.
   */
  conflict?: ConflictInfo;
}

export interface MetaEntry {
  /** Schlüssel wie 'shifts.lastSync', 'locations.lastSync' */
  key: string;
  value: string | number;
}

/**
 * Lokale Schicht (Spiegel des Servers oder optimistisches Insert).
 * Identisch zu `ApiShift`, plus interne Felder für Sync-Status.
 */
export interface LocalShift extends ApiShift {
  /** True solange Server-Bestätigung aussteht (Optimistisches Insert / Update). */
  pending?: boolean;
}

/** Lokaler Kalender-Eintrag (Notiz / Abwesenheit), plus Sync-Status. */
export interface LocalCalendarEntry extends ApiCalendarEntry {
  pending?: boolean;
}

/** Lokaler User-Eintrag (nur Anzeige; gemanaged vom Server). */
export type LocalUser = ApiUser;

/** Lokaler Standort. */
export type LocalLocation = ApiLocation;

/** Lokale Schicht-Vorlage. */
export type LocalTemplate = ApiShiftTemplate;

// ---------------------------------------------------------------------------

export class AppDB extends Dexie {
  shifts!: EntityTable<LocalShift, 'id'>;
  calendarEntries!: EntityTable<LocalCalendarEntry, 'id'>;
  locations!: EntityTable<LocalLocation, 'id'>;
  templates!: EntityTable<LocalTemplate, 'id'>;
  users!: EntityTable<LocalUser, 'id'>;
  outbox!: EntityTable<OutboxEntry, 'id'>;
  meta!: EntityTable<MetaEntry, 'key'>;

  constructor() {
    super('shiftsync');
    this.version(1).stores({
      // PK + Sekundär-Indizes für effiziente Abfragen
      shifts: 'id, userId, shiftDate, locationId, status, lastModified, deletedAt',
      locations: 'id, isActive',
      templates: 'id, locationId, isActive',
      users: 'id, email',
      outbox: '++id, kind, entityId, createdAt',
      meta: 'key',
    });
    // Schema-Version 2: Outbox bekommt das `conflict`-Feld (ohne Index — wird gefiltert).
    // Dexie migriert vorhandene Records mit, das `conflict`-Feld bleibt einfach undefined.
    this.version(2).stores({
      shifts: 'id, userId, shiftDate, locationId, status, lastModified, deletedAt',
      locations: 'id, isActive',
      templates: 'id, locationId, isActive',
      users: 'id, email',
      outbox: '++id, kind, entityId, createdAt',
      meta: 'key',
    });
    // Schema-Version 3: Schichten bekommen `settledAt`/`settledBy` (Abrechnung).
    // `settledAt` als Sekundär-Index für schnelle Offen/Abgerechnet-Filter.
    // Bestehende Records haben settledAt=undefined (= noch nicht abgerechnet);
    // Dexie migriert sie automatisch mit.
    this.version(3).stores({
      shifts: 'id, userId, shiftDate, locationId, status, settledAt, lastModified, deletedAt',
      locations: 'id, isActive',
      templates: 'id, locationId, isActive',
      users: 'id, email',
      outbox: '++id, kind, entityId, createdAt',
      meta: 'key',
    });
    // Schema-Version 4: Kalender-Einträge (Notiz/Frei/Urlaub/Krank/Wunschfrei).
    this.version(4).stores({
      shifts: 'id, userId, shiftDate, locationId, status, settledAt, lastModified, deletedAt',
      locations: 'id, isActive',
      templates: 'id, locationId, isActive',
      users: 'id, email',
      calendarEntries: 'id, userId, date, type, status, lastModified, deletedAt',
      outbox: '++id, kind, entityId, createdAt',
      meta: 'key',
    });
  }
}

export const db = new AppDB();
